// Sentry must initialize before the router module is evaluated so the
// React Router data-router integration can instrument the initial pageload and
// subsequent navigations. This file is imported first in main.tsx.
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { prepareFirebaseActionUrlForTelemetry } from "@/auth/firebaseActionUrlBootstrap";
import { normalizeDaleApiOrigin } from "@/config/daleApiUrl";
import {
  redactFirebaseActionCodeFromSentryEvent,
  sanitizeSentrySpan,
} from "@/utils/sentryAuthRedaction";
import { SENTRY_IGNORED_ERROR_PATTERNS } from "@/utils/sentryNoiseFilters";
import { safeConsole } from "./common/utils/safeConsole";

type SentryIntegration = ReturnType<
  typeof Sentry.reactRouterV7BrowserTracingIntegration
>;

export interface SentryInstrumentationSdk {
  init: typeof Sentry.init;
  reactRouterV7BrowserTracingIntegration: (
    options: Parameters<
      typeof Sentry.reactRouterV7BrowserTracingIntegration
    >[0],
  ) => SentryIntegration;
}

/** Sentry's v10 default key filter for headers and URL query parameters. */
const SENTRY_V10_PII_KEY_DENYLIST = [
  "forwarded",
  "-ip",
  "remote-",
  "via",
  "-user",
] as const;

const sentryInstrumentationSdk = {
  init: Sentry.init,
  reactRouterV7BrowserTracingIntegration:
    Sentry.reactRouterV7BrowserTracingIntegration,
} satisfies SentryInstrumentationSdk;

export interface SentryInstrumentationEnvironment {
  daleApiUrl?: string;
  dsn?: string;
  environment?: string;
  release?: string;
}

interface SentryInstrumentationBrowser {
  history: Parameters<typeof prepareFirebaseActionUrlForTelemetry>[0];
  location: Parameters<typeof prepareFirebaseActionUrlForTelemetry>[1];
}

interface SentryInstrumentationLogger {
  warn: typeof safeConsole.warn;
}

export const initializeSentryInstrumentation = ({
  browser,
  environment,
  logger,
  sentry,
}: {
  browser: SentryInstrumentationBrowser;
  environment: SentryInstrumentationEnvironment;
  logger: SentryInstrumentationLogger;
  sentry: SentryInstrumentationSdk;
}): void => {
  prepareFirebaseActionUrlForTelemetry(browser.history, browser.location);

  try {
    if (environment.dsn) {
      sentry.init({
        dsn: environment.dsn,
        // Sentry 11 collects every category below by default. Pin the
        // restrictive v10 baseline (Sentry's documented equivalent of
        // `sendDefaultPii: false`). This does not sanitize data supplied by
        // application code, so capture sites must still pass safe values only.
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: {
            request: { deny: [...SENTRY_V10_PII_KEY_DENYLIST] },
            response: { deny: [...SENTRY_V10_PII_KEY_DENYLIST] },
          },
          httpBodies: [],
          urlQueryParams: { deny: [...SENTRY_V10_PII_KEY_DENYLIST] },
          genAI: { inputs: false, outputs: false },
          databaseQueryData: false,
          queues: false,
          graphQL: { document: false, variables: false },
        },
        // Session Replay is intentionally NOT enabled. Replays capture DOM at
        // error time, including conversation transcripts, customer PII, and
        // form contents — even with maskAllText/maskAllInputs the masking has
        // known holes for custom components and the defaults shift between
        // SDK versions. We rely on stack traces + breadcrumbs + repro instead.
        integrations(defaultIntegrations) {
          return [
            // safeConsole records bounded, sanitized breadcrumbs directly. Keep
            // global console interception off until raw application calls are
            // migrated.
            ...defaultIntegrations.filter(
              (integration) => integration.name !== "Console",
            ),
            sentry.reactRouterV7BrowserTracingIntegration({
              useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
          ];
        },
        // Performance Monitoring — full sampling outside production, 20% in
        // production to keep quota in check now that every navigation is traced.
        tracesSampleRate: environment.environment === "production" ? 0.2 : 1.0,
        // Propagate distributed-trace headers only to localhost (dev proxy) and
        // the configured backend for this build. Sourced from the same env var
        // as the auth-origin guard so both stay in lockstep. Filter out the env
        // var if it's missing at build time — undefined in this list silently
        // breaks Sentry header propagation.
        tracePropagationTargets: [
          "localhost",
          normalizeDaleApiOrigin(environment.daleApiUrl),
        ].filter((t): t is string => typeof t === "string" && t.length > 0),
        environment: environment.environment,
        release: environment.release || undefined,
        // Browser and vendor self-reports that are not faults in this app. Each
        // is matched on its message rather than its error type, because every one
        // of them shares a constructor name with something actionable — see
        // `sentryNoiseFilters.ts`.
        ignoreErrors: [...SENTRY_IGNORED_ERROR_PATTERNS],
        beforeSend: redactFirebaseActionCodeFromSentryEvent,
        // Spans stream by default, so no transaction events exist: this also
        // covers the segment span that carries the page URL.
        beforeSendSpan: sanitizeSentrySpan,
      });
    }
  } catch {
    logger.warn("application-lifecycle", "Sentry initialization failed.");
  }
};

if (globalThis.window !== undefined) {
  initializeSentryInstrumentation({
    browser: { history: window.history, location: window.location },
    environment: {
      daleApiUrl: import.meta.env.VITE_DALE_API_URL,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_PUBLIC_ENVIRONMENT,
      release: import.meta.env.VITE_SENTRY_RELEASE,
    },
    logger: safeConsole,
    sentry: sentryInstrumentationSdk,
  });
}
